import React, { useEffect, useState } from 'react';
import { Drawer, Form, InputNumber, Button, message, Slider, Checkbox, Select } from 'antd';
import { useCanvas } from '../../context/canvasContext';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { emitCanvasData, emitDeleteObject } from '../../socket';
import './propertiesDrawer.css';
import { boardServices } from '../../api';
import { useDocument } from '../../context/documentInfoContex';
import { isSupportedObject } from '../../context/utils/CanvasHelper';
import { isOffline } from '../../utils/helper';

const { Option } = Select;

const ColorInput = ({ value, onChange }) => (
  <div className="color-picker-container">
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: 24,
        height: 24,
        padding: 0,
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        background: 'none',
        cursor: 'pointer',
      }}
    />
    <div className="color-preview" style={{ backgroundColor: value }} />
  </div>
);

const PropertiesModal = ({ isOpen, onClose }) => {
  const { canvasRef, saveStateToUndoStack } = useCanvas();
  const { setSavingData } = useDocument();
  const { id } = useParams();
  const roomID = id;
  const [form] = Form.useForm();
  const [objectExists, setObjectExists] = useState(false);
  const [objectType, setObjectType] = useState(null);
  const [opacityValue, setOpacityValue] = useState(100);
  const [rotationValue, setRotationValue] = useState(0);
  const [borderRadiusUnit, setBorderRadiusUnit] = useState('px');
  const [strokeWidthUnit, setStrokeWidthUnit] = useState('px');

  useEffect(() => {
    const element = canvasRef.current?.getActiveObject();
    if (!element || !isSupportedObject(element)) {
      setObjectExists(false);
      setObjectType(null);
      form.resetFields();
      return;
    }

    setObjectExists(true);
    setObjectType(element.type);
    setOpacityValue(Math.round(element.opacity * 100));
    setRotationValue(element.angle || 0);

    // Initialize properties
    let borderRadiusValue = element.rx || 0;
    let strokeWidthValue = element.strokeWidth || 0;
    const storedBorderRadiusUnit = element.borderRadiusUnit || 'px';
    const storedStrokeWidthUnit = element.strokeWidthUnit || 'px';

    // Convert stored pixel values to selected unit if needed (for shapes only)
    if (element.type !== 'image') {
      if (storedBorderRadiusUnit === '%') {
        borderRadiusValue = (element.rx / canvasRef.current.getWidth()) * 100;
      } else if (storedBorderRadiusUnit === 'rem') {
        borderRadiusValue = element.rx / 16;
      }

      if (storedStrokeWidthUnit === '%') {
        strokeWidthValue = (element.strokeWidth / canvasRef.current.getWidth()) * 100;
      } else if (storedStrokeWidthUnit === 'rem') {
        strokeWidthValue = element.strokeWidth / 16;
      }
    }

    setBorderRadiusUnit(storedBorderRadiusUnit);
    setStrokeWidthUnit(storedStrokeWidthUnit);

    form.setFieldsValue({
      width: element.width * element.scaleX,
      height: element.height * element.scaleY,
      left: element.left,
      top: element.top,
      fill: element.fill || '#ffffff',
      stroke: element.stroke || '#000000',
      strokeWidth: Number(strokeWidthValue.toFixed(2)),
      strokeWidthUnit: storedStrokeWidthUnit,
      borderRadius: Number(borderRadiusValue.toFixed(2)),
      borderRadiusUnit: storedBorderRadiusUnit,
      opacity: Math.round(element.opacity * 100),
      rotation: element.angle || 0,
      isTransparent: !element.fill,
      borderStyle: element.strokeDashArray
        ? element.strokeDashArray[0] === 10
          ? 'dashed'
          : 'dotted'
        : element.strokeWidth === 0
          ? 'none'
          : 'solid',
    });
  }, [isOpen, form, canvasRef]);

  const handleUpdate = async () => {
    const element = canvasRef.current?.getActiveObject();
    if (!element) return;

    try {
      const values = await form.validateFields();

      // Convert border radius to pixels for canvas (shapes only)
      let borderRadiusInPixels = element.type !== 'image' ? values.borderRadius : 0;
      if (element.type !== 'image' && values.borderRadiusUnit === '%') {
        borderRadiusInPixels = (values.borderRadius / 100) * canvasRef.current.getWidth();
      } else if (element.type !== 'image' && values.borderRadiusUnit === 'rem') {
        borderRadiusInPixels = values.borderRadius * 16;
      }

      // Convert stroke width to pixels for canvas
      let strokeWidthInPixels = values.strokeWidth;
      if (values.strokeWidthUnit === '%') {
        strokeWidthInPixels = (values.strokeWidth / 100) * canvasRef.current.getWidth();
      } else if (values.strokeWidthUnit === 'rem') {
        strokeWidthInPixels = values.strokeWidth * 16;
      }

      // Set strokeDashArray based on borderStyle
      let strokeDashArray = null;
      if (values.borderStyle === 'dashed') {
        strokeDashArray = [10, 5];
      } else if (values.borderStyle === 'dotted') {
        strokeDashArray = [2, 2];
      } else if (values.borderStyle === 'none') {
        strokeWidthInPixels = 0;
      }

      // Handle resizing for images and shapes differently
      let updateProps = {
        left: values.left,
        top: values.top,
        opacity: values.opacity / 100,
        angle: values.rotation,
      };

      if (element.type === 'image') {
        // For images, adjust scaleX and scaleY to resize the actual image
        const naturalWidth = element.width; // Original image width
        const naturalHeight = element.height; // Original image height
        updateProps.scaleX = values.width / naturalWidth;
        updateProps.scaleY = values.height / naturalHeight;
      } else {
        // For shapes, adjust width and height directly
        updateProps.width = values.width / element.scaleX;
        updateProps.height = values.height / element.scaleY;
        updateProps.fill = values.isTransparent ? null : values.fill;
        updateProps.stroke = values.borderStyle === 'none' ? null : values.stroke;
        updateProps.strokeWidth = strokeWidthInPixels;
        updateProps.rx = borderRadiusInPixels;
        updateProps.ry = borderRadiusInPixels;
        updateProps.strokeDashArray = strokeDashArray;
        updateProps.borderRadiusUnit = values.borderRadiusUnit;
        updateProps.strokeWidthUnit = values.strokeWidthUnit;
      }

      element.set(updateProps);

      const canvasData = canvasRef.current.toJSON([
        'selectable',
        'id',
        'type',
        'angle',
        'scaleX',
        'scaleY',
        'left',
        'top',
        'fill',
        'stroke',
        'strokeWidth',
        'rx',
        'ry',
        'strokeDashArray',
        'borderRadiusUnit',
        'strokeWidthUnit',
      ]);
      boardServices.updateBoardData(id, canvasData, setSavingData);
      canvasRef.current.renderAll();
      !isOffline() && emitCanvasData(roomID, canvasData);
      saveStateToUndoStack();
      message.success('Object updated successfully');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = () => {
    const element = canvasRef.current?.getActiveObject();
    if (element) {
      deleteSelected();
      onClose();
    }
  };

  const deleteSelected = () => {
    const activeObjects = canvasRef.current._activeObject;
    if (activeObjects) {
      if (activeObjects.type === 'group') {
        let removedObjID = activeObjects.id;
        canvasRef.current.remove(activeObjects);
        !isOffline() && emitDeleteObject(removedObjID);
      } else if (activeObjects._objects) {
        activeObjects._objects.forEach((object) => {
          let removedObjID = object.id;
          canvasRef.current.remove(object);
          !isOffline() && emitDeleteObject(removedObjID);
        });
      } else {
        let removedObjID = activeObjects.id;
        canvasRef.current.remove(activeObjects);
        !isOffline() && emitDeleteObject(removedObjID);
      }
    }
    let canvasData = canvasRef.current.toJSON([
      'selectable',
      'id',
      'type',
      'angle',
      'scaleX',
      'scaleY',
      'left',
      'top',
      'fill',
      'stroke',
      'strokeWidth',
      'rx',
      'ry',
      'strokeDashArray',
      'borderRadiusUnit',
      'strokeWidthUnit',
    ]);
    !isOffline() && emitCanvasData(id, canvasData);
    canvasRef.current.discardActiveObject().renderAll();
    saveStateToUndoStack();
  };

  return (
    <Drawer
      title="Object Properties"
      placement="right"
      width={360}
      onClose={onClose}
      visible={isOpen}
      destroyOnClose
      className="properties-drawer"
      footer={
        <div className="drawer-footer">
          <Button
            className="footer-btn delete-btn btn-style"
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button
            className="update-btn btn-style"
            onClick={handleUpdate}
            type="primary"
          >
            Update
          </Button>
        </div>
      }
    >
      {!objectExists ? (
        <div>No object selected or unsupported object.</div>
      ) : (
        <Form form={form} layout="vertical" className="properties-form">
          <div className="section-title">Dimensions</div>
          <div className="dimensions-group">
            <div className="dimensions-fields">
              <Form.Item
                label="Width (px)"
                name="width"
                rules={[{ required: true, message: 'Width is required' }]}
                className="dimensions-item"
              >
                <InputNumber min={1} precision={3} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label="Height (px)"
                name="height"
                rules={[{ required: true, message: 'Height is required' }]}
                className="dimensions-item"
              >
                <InputNumber min={1} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div className="dimensions-fields">
              <Form.Item
                label="Left (px)"
                name="left"
                rules={[{ required: true, message: 'Left is required' }]}
                className="dimensions-item"
              >
                <InputNumber precision={3} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label="Top (px)"
                name="top"
                rules={[{ required: true, message: 'Top is required' }]}
                className="dimensions-item"
              >
                <InputNumber precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </div>

          <div className="section-divider" />

          <div className="section-title">Appearance</div>
          <Form.Item label="Opacity" name="opacity" className="slider-row">
            <Slider
              min={0}
              max={100}
              step={1}
              value={opacityValue}
              onChange={(value) => {
                setOpacityValue(value);
                form.setFieldsValue({ opacity: value });
              }}
              tooltip={{ formatter: (value) => `${value}%` }}
            />
            <span className="slider-value">{opacityValue}%</span>
          </Form.Item>
          <Form.Item label="Rotation" name="rotation" className="slider-row">
            <Slider
              min={0}
              max={360}
              step={1}
              value={rotationValue}
              onChange={(value) => {
                setRotationValue(value);
                form.setFieldsValue({ rotation: value });
              }}
              tooltip={{ formatter: (value) => `${value}°` }}
            />
            <span className="slider-value">{rotationValue}°</span>
          </Form.Item>

          {objectType !== 'image' && (
            <>
              <Form.Item label="Background Color" name="fill" className="color-item">
                <ColorInput />
              </Form.Item>
              <Form.Item label="Border Color" name="stroke" className="color-item">
                <ColorInput />
              </Form.Item>
              <Form.Item name="isTransparent" valuePropName="checked" className="checkbox-item">
                <Checkbox>Transparent</Checkbox>
              </Form.Item>
              <Form.Item label="Border Width" className="compact-form-item">
                <div className="stroke-width-container">
                  <Form.Item
                    name="strokeWidth"
                    noStyle
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={0} step={0.1} precision={2} className="stroke-width-input" />
                  </Form.Item>
                  <Form.Item name="strokeWidthUnit" noStyle>
                    <Select
                      value={strokeWidthUnit}
                      onChange={setStrokeWidthUnit}
                      className="stroke-unit-select"
                    >
                      <Option value="px">px</Option>
                      <Option value="%">%</Option>
                      <Option value="rem">rem</Option>
                    </Select>
                  </Form.Item>
                </div>
              </Form.Item>
              <Form.Item label="Border Radius" className="compact-form-item">
                <div className="stroke-width-container">
                  <Form.Item
                    name="borderRadius"
                    noStyle
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={0} step={0.1} precision={2} className="stroke-width-input" />
                  </Form.Item>
                  <Form.Item name="borderRadiusUnit" noStyle>
                    <Select
                      value={borderRadiusUnit}
                      onChange={setBorderRadiusUnit}
                      className="stroke-unit-select"
                    >
                      <Option value="px">px</Option>
                      <Option value="%">%</Option>
                      <Option value="rem">rem</Option>
                    </Select>
                  </Form.Item>
                </div>
              </Form.Item>
              <Form.Item label="Border Style" name="borderStyle" className="compact-form-item">
                <Select className="stroke-unit-select">
                  <Option value="solid">Solid</Option>
                  <Option value="dashed">Dashed</Option>
                  <Option value="dotted">Dotted</Option>
                  <Option value="none">None</Option>
                </Select>
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Drawer>
  );
};

export default PropertiesModal;